"use client";

import { useEffect, useRef } from "react";
import { useRouter } from "next/navigation";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useQuery } from "@tanstack/react-query";
import { useSession } from "next-auth/react";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Form } from "@/components/ui/form";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import {
  SelectField,
  TextareaField,
  TextField,
  SwitchField,
  S3UploadField,
} from "@admin/_components/form-fields";
import { useApiPost } from "@admin/_lib/api-hooks";

// ── Schema ────────────────────────────────────────────────────────────────────

const schema = z
  .object({
    sign_id: z.string().min(1, "Sign is required"),
    decan: z.string().min(1, "Decan is required"),
    content_type: z.string().min(1, "Content type is required"),
    // Uploaded Video branch (stored in content.*)
    video_title: z.string().optional().default(""),
    video_description: z.string().optional().default(""),
    thumbnail: z.array(z.any()).default([]),
    video_file: z.array(z.any()).default([]),
    // YouTube Video branch
    youtube_url: z.string().optional().default(""),
    youtube_title: z.string().optional().default(""),
    youtube_description: z.string().optional().default(""),
    // Pronouncement branch
    pronuncement_name: z.string().optional().default(""),
    pron_description: z.string().optional().default(""),
    assets: z.array(z.any()).default([]),
    // Common
    status: z.boolean().default(true),
  })
  .superRefine((data, ctx) => {
    if (data.content_type === "YouTube Video" && !data.youtube_url?.trim()) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "YouTube URL is required", path: ["youtube_url"] });
    }
    if (data.content_type === "add pronouncement" && !data.pronuncement_name?.trim()) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Name is required", path: ["pronuncement_name"] });
    }
  });

type FormValues = z.infer<typeof schema>;

interface SignOption { _id: string; title: string }

function getBaseUrl() {
  return process.env.NEXT_PUBLIC_API_CLOUDFRONT ?? "https://api.divineinfinitebeing.com/";
}

// ── DecanVideoForm ─────────────────────────────────────────────────────────────

export function DecanVideoForm({ id }: { id?: string }) {
  const router = useRouter();
  const isEdit = !!id;
  const { data: session } = useSession();
  const baseUrl = getBaseUrl();
  const isFirstRender = useRef(true);

  // Fetch sign options (wheel-sign-autocomplete returns json.res)
  const { data: signOptions = [] } = useQuery<SignOption[]>({
    queryKey: ["wheel-sign-autocomplete"],
    queryFn: async () => {
      const res = await fetch(`${baseUrl}wheel_signs/wheel-sign-autocomplete`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.token}`,
        },
        body: JSON.stringify({
          condition: { status: 1, limit: 200, skip: 0 },
          searchcondition: {},
          sort: { type: "asc", field: "name" },
          project: {},
          token: "",
        }),
      });
      const json = await res.json();
      const result = json.res ?? json.data ?? json.results?.res ?? json.results ?? json;
      return Array.isArray(result) ? result : [];
    },
    enabled: !!session?.token,
    staleTime: 5 * 60 * 1000,
  });

  // Fetch existing record for edit
  const { data: record, isLoading: isFetching } = useQuery({
    queryKey: ["decan-video-single", id],
    queryFn: async () => {
      const res = await fetch(`${baseUrl}wheel_signs/single-video-pronuncement-fetch`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.token}`,
        },
        body: JSON.stringify({ _id: id }),
      });
      const data = await res.json();
      const result = data.results?.res ?? data.results ?? data.res ?? data;
      return Array.isArray(result) ? result[0] : result;
    },
    enabled: isEdit && !!session?.token,
  });

  const mutation = useApiPost("wheel_signs/astro-terrot-decan-image-video-info-add-edit", {
    invalidateKeys: ["wheel_signs/astro-decan-video-pronumcement-list"],
  });

  const form = useForm<FormValues>({
    resolver: zodResolver(schema) as any,
    defaultValues: {
      sign_id: "",
      decan: "",
      content_type: "",
      video_title: "",
      video_description: "",
      thumbnail: [],
      video_file: [],
      youtube_url: "",
      youtube_title: "",
      youtube_description: "",
      pronuncement_name: "",
      pron_description: "",
      assets: [],
      status: true,
    },
  });

  const contentType = form.watch("content_type");

  // DV-03: hydrate from nested content.* fields on edit
  useEffect(() => {
    if (!record) return;
    isFirstRender.current = false;

    const content = (record.content ?? {}) as Record<string, unknown>;

    const thumbArr = Array.isArray(content.thumbnail_path)
      ? content.thumbnail_path
      : content.thumbnail_path ? [content.thumbnail_path] : [];
    const videoArr = Array.isArray(content.video)
      ? content.video
      : content.video ? [content.video] : [];
    const assetsArr = Array.isArray(content.assets)
      ? content.assets
      : content.assets ? [content.assets] : [];

    form.reset({
      // sign_id from record.sign_id (the stored ID); fallback to matching title
      sign_id: record.sign_id ? String(record.sign_id) : "",
      decan: String(record.decan ?? ""),
      content_type: String(record.content_type ?? ""),
      video_title: String(content.title ?? record.video_title ?? ""),
      video_description: String(content.description ?? record.video_description ?? ""),
      thumbnail: thumbArr,
      video_file: videoArr,
      youtube_url: String(content.youtube_url ?? record.youtube_url ?? ""),
      youtube_title: String(content.title ?? record.youtube_title ?? ""),
      youtube_description: String(content.description ?? record.youtube_description ?? ""),
      pronuncement_name: String(content.pronuncement_name ?? record.name ?? ""),
      pron_description: String(content.pronuncement_description ?? record.pron_description ?? ""),
      assets: assetsArr,
      status: record.status === 1 || record.status === true,
    });
  }, [record, form]);

  async function onSubmit(values: FormValues) {
    try {
      // DV-03: Angular sends sign_id + sign label + nested content object
      const selectedSign = signOptions.find((o) => o._id === values.sign_id);
      const signLabel = selectedSign?.title ?? "";

      // Build nested content based on branch
      const content: Record<string, unknown> = {};
      if (values.content_type === "uploaded Video") {
        content.title = values.video_title;
        content.description = values.video_description;
        content.thumbnail_path = values.thumbnail;
        content.video = values.video_file;
      } else if (values.content_type === "YouTube Video") {
        content.title = values.youtube_title;
        content.description = values.youtube_description;
        content.youtube_url = values.youtube_url;
      } else if (values.content_type === "add pronouncement") {
        content.pronuncement_name = values.pronuncement_name;
        content.pronuncement_description = values.pron_description;
        content.assets = values.assets;
      }

      const body: Record<string, unknown> = {
        sign_id: values.sign_id,
        sign: signLabel,
        decan: values.decan,
        content_type: values.content_type,
        content,
        status: values.status ? 1 : 0,
      };

      if (isEdit) body._id = id;

      await mutation.mutateAsync(body as any);
      toast.success(isEdit ? "Updated successfully" : "Created successfully");
      router.push("/admin/astrology/decan-videos");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Operation failed");
    }
  }

  if (isEdit && isFetching) {
    return (
      <div className="space-y-4">
        <div className="h-10 w-64 animate-pulse rounded-md" style={{ background: "hsl(240 20% 16%)" }} />
        <div className="h-96 animate-pulse rounded-xl" style={{ background: "hsl(240 20% 16%)" }} />
      </div>
    );
  }

  const signSelectOptions = signOptions.map((o) => ({ value: o._id, label: o.title }));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" className="shrink-0" onClick={() => router.back()}>
          <ArrowLeft className="size-4" />
        </Button>
        <div className="h-6 w-1 rounded-full shrink-0" style={{ background: "linear-gradient(to bottom, #ffa800, #ff7e00)" }} />
        <div>
          <h1 className="text-xl font-semibold text-foreground">
            {isEdit ? "Edit Decan Content" : "Add Decan Content"}
          </h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            {isEdit ? "Update decan video or pronunciation" : "Create a new decan video or pronunciation"}
          </p>
        </div>
      </div>

      <Card>
        <CardHeader className="border-b" style={{ borderColor: "hsl(240 20% 20%)" }}>
          <CardTitle className="flex items-center gap-2 text-base">
            <span className="h-4 w-0.5 rounded-full" style={{ background: "#ffa800" }} />
            Decan Details
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <div className="grid gap-4 md:grid-cols-2">
                {/* DV-02: sign from wheel-sign-autocomplete, stores _id */}
                <SelectField
                  control={form.control}
                  name="sign_id"
                  label="Sign"
                  placeholder="Select sign"
                  options={signSelectOptions}
                />

                <SelectField
                  control={form.control}
                  name="decan"
                  label="Decan"
                  placeholder="Select decan"
                  options={[
                    { value: "First Decan", label: "First Decan" },
                    { value: "Second Decan", label: "Second Decan" },
                    { value: "Third Decan", label: "Third Decan" },
                  ]}
                />

                <div className="md:col-span-2">
                  <SelectField
                    control={form.control}
                    name="content_type"
                    label="Content Type"
                    placeholder="Select content type"
                    options={[
                      { value: "uploaded Video", label: "Uploaded Video" },
                      { value: "YouTube Video", label: "YouTube Video" },
                      { value: "add pronouncement", label: "Add Pronouncement" },
                    ]}
                  />
                </div>

                {/* ── Uploaded Video branch ── */}
                {contentType === "uploaded Video" && (
                  <>
                    <TextField
                      control={form.control}
                      name="video_title"
                      label="Video Title"
                      placeholder="Enter video title"
                    />
                    <div className="md:col-span-2">
                      <TextareaField
                        control={form.control}
                        name="video_description"
                        label="Video Description"
                        placeholder="Describe the video..."
                      />
                    </div>
                    <S3UploadField
                      control={form.control}
                      name="thumbnail"
                      label="Thumbnail Image"
                      bucket="all-frontend-assets"
                      path="divine-infinity-being/tarrot_video_thumb_url/"
                      accept="image/*"
                    />
                    <S3UploadField
                      control={form.control}
                      name="video_file"
                      label="Video File"
                      bucket="all-frontend-assets"
                      path="divine-infinity-being/video-management/"
                      accept="video/*"
                    />
                  </>
                )}

                {/* ── YouTube Video branch ── */}
                {contentType === "YouTube Video" && (
                  <>
                    <TextField
                      control={form.control}
                      name="youtube_title"
                      label="Title"
                      placeholder="Video title"
                    />
                    <div className="md:col-span-2">
                      <TextareaField
                        control={form.control}
                        name="youtube_description"
                        label="Description"
                        placeholder="Describe the video..."
                      />
                    </div>
                    <div className="md:col-span-2">
                      <TextField
                        control={form.control}
                        name="youtube_url"
                        label="YouTube URL"
                        placeholder="https://www.youtube.com/watch?v=..."
                      />
                    </div>
                  </>
                )}

                {/* ── Pronouncement branch ── */}
                {contentType === "add pronouncement" && (
                  <>
                    <TextField
                      control={form.control}
                      name="pronuncement_name"
                      label="Pronouncement Name"
                      placeholder="Pronouncement name"
                    />
                    <div className="md:col-span-2">
                      <TextareaField
                        control={form.control}
                        name="pron_description"
                        label="Pronouncement Description"
                        placeholder="Describe the pronouncement..."
                      />
                    </div>
                    <div className="md:col-span-2">
                      <S3UploadField
                        control={form.control}
                        name="assets"
                        label="Assets"
                        bucket="all-frontend-assets"
                        path="divine-infinity-being/"
                      />
                    </div>
                  </>
                )}

                <div className="md:col-span-2">
                  <SwitchField
                    control={form.control}
                    name="status"
                    label="Active"
                    description="Make this content visible"
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="submit"
                  disabled={mutation.isPending}
                  className="inline-flex items-center gap-2 rounded-md px-5 py-2 text-sm font-semibold transition-all disabled:opacity-60 disabled:cursor-not-allowed btn-orange"
                >
                  {mutation.isPending ? "Saving..." : isEdit ? "Update" : "Create"}
                </button>
                <Button type="button" variant="outline" onClick={() => router.back()}>
                  Cancel
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
